import Table, { TableBody, TableHeader, TableHeaderColumn } from '@rio-cloud/rio-uikit/Table';
import type { TableViewTogglesViewType } from '@rio-cloud/rio-uikit/TableViewToggles';
import classNames from 'classnames';
import { type ReactNode, type Ref, useMemo } from 'react';
import type { IntlShape } from 'react-intl';

import type { OrderType } from '#domain/Order.ts';
import type { OrderWithSortableKey } from '#utils/orders.util.ts';

import { NoDataView } from '../shared/NoDataView.tsx';
import { OrderEmptyState } from './OrderEmptyState.tsx';
import { OrderTableRow } from './OrderTableRow.tsx';
import OrderTableSelectDropDownMenu from './OrderTableSelectDropDownMenu.tsx';

type OrderTableProps = {
    activeOrderId?: string;
    hasNoOrders: boolean;
    onDeselectAllOpenOrders: () => void;
    onCancelSelectedOpenOrders: () => void;
    onOpenCreateOrderDialog: (type?: OrderType) => void;
    onOpenCreateTourDialog: () => void;
    onColumnResize: (columnKey: string, width?: number | string) => void;
    onRowClick: (orderId: string) => void;
    onViewTypeChange: (viewType: TableViewTogglesViewType) => void;
    onSelectAllOpenOrders: () => void;
    onSelectionChange: (rowIds: string[] | null) => void;
    onColumnOrderChange: (columnOrder: string[]) => void;
    onSortChange: (columnKey: keyof OrderWithSortableKey, direction: 'asc' | 'desc') => void;
    orders: OrderWithSortableKey[];
    selectableOrdersCount: number;
    selectedOrderIds: string[];
    selectedOrdersCount: number;
    sortDirection: 'asc' | 'desc';
    sortKey: keyof OrderWithSortableKey;
    tableRef: Ref<HTMLDivElement>;
    columns: OrderTableColumnConfig[];
    viewType: TableViewTogglesViewType;
};

export type OrderTableColumnKey =
    | 'type'
    | 'number'
    | 'customerName'
    | 'loadingAddressEndDate'
    | 'unloadingAddressEndDate'
    | 'tourName'
    | 'truckName'
    | 'statusWeight';

export type OrderTableColumnConfig = {
    key: OrderTableColumnKey;
    label: ReactNode;
    disabled?: boolean;
    draggable?: boolean;
    visible: boolean;
    resizeable?: boolean;
    sortable?: boolean;
    width?: number | string;
};

export const getOrderTableColumns = (intl: IntlShape, showServiceOrders = true): OrderTableColumnConfig[] => [
    ...(showServiceOrders
        ? [
              {
                  key: 'type' as const,
                  label: intl.formatMessage({
                      id: 'intl-msg:ordercom-cloud-frontend.orders.table.header.type',
                      defaultMessage: 'Type',
                  }),
                  disabled: true,
                  visible: true,
                  width: 55,
              },
          ]
        : []),
    {
        key: 'number',
        label: intl.formatMessage({
            id: 'intl-msg:ordercom-cloud-frontend.orders.table.header.order',
            defaultMessage: 'Order',
        }),
        draggable: true,
        visible: true,
        resizeable: true,
        sortable: true,
        width: 100,
    },
    {
        key: 'customerName',
        label: intl.formatMessage({
            id: 'intl-msg:ordercom-cloud-frontend.orders.table.header.customer',
            defaultMessage: 'Customer',
        }),
        draggable: true,
        visible: true,
        resizeable: true,
        sortable: true,
        width: 200,
    },
    {
        key: 'loadingAddressEndDate',
        label: intl.formatMessage({
            id: 'intl-msg:ordercom-cloud-frontend.orders.table.header.pick-up',
            defaultMessage: 'Pick-up',
        }),
        draggable: true,
        visible: true,
        resizeable: true,
        sortable: true,
        width: 300,
    },
    {
        key: 'unloadingAddressEndDate',
        label: intl.formatMessage({
            id: 'intl-msg:ordercom-cloud-frontend.orders.table.header.delivery',
            defaultMessage: 'Delivery',
        }),
        draggable: true,
        visible: true,
        resizeable: true,
        sortable: true,
        width: 300,
    },
    {
        key: 'tourName',
        label: intl.formatMessage({
            id: 'intl-msg:ordercom-cloud-frontend.orders.table.header.tour',
            defaultMessage: 'Tour',
        }),
        draggable: true,
        visible: true,
        resizeable: true,
        sortable: true,
        width: 200,
    },
    {
        key: 'truckName',
        label: intl.formatMessage({
            id: 'intl-msg:ordercom-cloud-frontend.orders.table.header.vehicle',
            defaultMessage: 'Vehicle',
        }),
        draggable: true,
        visible: true,
        resizeable: true,
        sortable: true,
        width: 150,
    },
    {
        key: 'statusWeight',
        label: intl.formatMessage({
            id: 'intl-msg:ordercom-cloud-frontend.orders.table.header.status',
            defaultMessage: 'Status',
        }),
        draggable: true,
        visible: true,
    },
];

export const OrderTable = ({
    activeOrderId,
    hasNoOrders,
    onDeselectAllOpenOrders,
    onCancelSelectedOpenOrders,
    onOpenCreateOrderDialog,
    onOpenCreateTourDialog,
    onColumnResize,
    onRowClick,
    onViewTypeChange,
    onSelectAllOpenOrders,
    onSelectionChange,
    onColumnOrderChange,
    onSortChange,
    orders,
    selectableOrdersCount,
    selectedOrderIds,
    selectedOrdersCount,
    sortDirection,
    sortKey,
    tableRef,
    columns,
    viewType,
}: OrderTableProps) => {
    const isTableEmpty = orders.length === 0;
    const visibleColumns = columns.filter(column => column.visible !== false);
    const visibleColumnKeys = useMemo(() => visibleColumns.map(column => column.key), [visibleColumns]);

    return (
        <Table
            ref={tableRef}
            rowKey='id'
            className={classNames('table-head-filled table-column-overflow-hidden')}
            hover={!isTableEmpty}
            activeRowId={activeOrderId}
            viewType={viewType}
            sortBy={String(sortKey)}
            sortDirection={sortDirection}
            onSortChange={(columnKey, direction) => onSortChange(columnKey as keyof OrderWithSortableKey, direction)}
            columnOrder={columns.map(column => column.key)}
            onColumnOrderChange={onColumnOrderChange}
            onColumnResize={onColumnResize}
            onViewTypeChange={onViewTypeChange}
            onRowClick={row => onRowClick(String(row.id))}
            showSelectionColumn
            hideSelectionCheckboxesForExcludedRows
            selectedRowIds={selectedOrderIds}
            onSelectionChange={rowIds => onSelectionChange(rowIds.length ? rowIds.map(String) : null)}
            responsiveBreakpoint={{ list: '800px' }}
            selectionHeaderContent={
                <OrderTableSelectDropDownMenu
                    onOpenCreateTourDialog={onOpenCreateTourDialog}
                    onSelectAllOpenOrders={onSelectAllOpenOrders}
                    onDeselectAllOpenOrders={onDeselectAllOpenOrders}
                    onCancelSelectedOpenOrders={onCancelSelectedOpenOrders}
                    selectedOrdersCount={selectedOrdersCount}
                    selectableOrdersCount={selectableOrdersCount}
                />
            }
            noRowsState={
                hasNoOrders ? <OrderEmptyState onOpenCreateOrderDialog={onOpenCreateOrderDialog} /> : <NoDataView />
            }
        >
            <TableHeader sticky>
                {visibleColumns.map(column => (
                    <TableHeaderColumn
                        key={column.key}
                        columnKey={column.key}
                        width={column.width}
                        sortable={column.sortable}
                        resizeable={column.resizeable}
                        draggable={column.draggable}
                    >
                        {column.label}
                    </TableHeaderColumn>
                ))}
            </TableHeader>
            <TableBody>
                {orders.map(order => (
                    <OrderTableRow order={order} key={order.id} visibleColumnKeys={visibleColumnKeys} />
                ))}
            </TableBody>
        </Table>
    );
};
